// Every project, screen and version in Loft, in one place.
//
// The top level is projects, matching the Projects list on the ProdEng Home Page
// in Notion: Tell Us Once, Project Nails, Project MC, CAM Admin, IFA / Partner
// Portal, Meaningful Interactions. An earlier pass grouped by app surface
// instead, which put CAM Admin and North Star at the same level as the member
// app when both are projects, and left Actions and Care loose when they are two
// thirds of Meaningful Interactions.
//
// Nothing here is a hub. Every entry opens a prototype, and the only pages Loft
// has of its own are the landing page and its documentation. Routes, redirects
// and the menu are all generated from this file.

import { SURFACE, type Accent, type Surface } from '@/designs-dev/loft/accents';

/**
 * What a version claims about itself. Two states, not one: `live` is what is in
 * production today, `backed` is the proposal being pushed. A version-less URL
 * resolves to `backed` if there is one, otherwise `live`.
 */
export type VersionState = 'live' | 'backed' | 'proposal' | 'archived';

export type Screen = {
  /** URL segment, or segments — the master's screens nest. */
  slug: string;
  label: string;
  /**
   * The path this screen has today, for the generated redirect and to tell the
   * route builder which element to mount. Absent for a screen that is new.
   */
  legacy?: string;
  /**
   * A full path to a screen another project owns, so a project can list what it
   * covers without claiming the URL. Member-app screens are the common case:
   * the app owns them, and a project points at them. loft:check asserts every
   * one of these still resolves.
   */
  canonical?: string;
  /**
   * The screen runs its own routes underneath its path, so it is registered with
   * a splat. Magic capture is the case: it walks a journey of its own and each
   * step is addressable.
   */
  nested?: boolean;
  /**
   * Two screens are takes on the same thing when they share this. They get a
   * version switch in the chrome above the prototype, beside the drill toggle,
   * and each one's `note` says what is different about it.
   *
   * The Keni and Benefits journeys are why: both open on the same landing
   * screen, so from the menu they were indistinguishable and you had to click
   * into each to find out which was which.
   */
  variantOf?: string;
  /** Short name for this take, for the version switch. */
  variantLabel?: string;
  /**
   * Paths this screen used to own, for the generated redirects. `legacy` is the
   * one path the route builder also reads to find the element, so a screen that
   * has moved twice keeps the rest here.
   */
  legacyPaths?: string[];
  /** What this screen is, or how it differs from its siblings. */
  note?: string;
  /**
   * Draw a rule under this row in the menu. A group of screens that belong
   * together reads as one thing when a line closes it, the way a divider does
   * in a Figma layer list.
   */
  dividerAfter?: boolean;
  /**
   * Put this screen above the project's flows pages in the menu. Flows lead by
   * default, which is right when the flows page is the work; it is wrong when
   * the prototype is the thing and the flows page is the reading of it.
   */
  leads?: boolean;
  /**
   * A flows page id. The screen leaves the leads group and sits directly above
   * that flows page in the menu instead, for a screen that belongs to one piece
   * of the work rather than the whole project.
   */
  pairsWith?: string;
  /**
   * A flows page id. Like `pairsWith`, but the screen sits directly below that
   * flows page, inside its group: a rule under the flows page moves down to
   * close the group under the screen instead.
   */
  follows?: string;
  /**
   * The project's plan: what the work is for, the order it gets built in, and
   * what is still open. A document rather than a prototype.
   *
   * It reads first in the menu and is deliberately not what the project's row
   * opens. Every entry in Loft opens something you can look at, and somebody
   * clicking Tell Us Once wants the screens, not the reasoning behind them.
   */
  plan?: boolean;
  /**
   * This screen lays out its own stage: a full-bleed surface that paints the
   * page behind itself and expects the whole window.
   *
   * The chrome centres a screen at its natural size, which is right for a phone
   * frame and wrong for one of these: contained, the page colour it paints
   * stops being the page and becomes a grey box behind the design. So the
   * chrome stretches it and floats the screen select over the top instead.
   */
  fills?: boolean;
};

export type Version = {
  n: number;
  /** Required. There is no version without a line saying what changed. */
  note: string;
  state: VersionState;
  screens: Screen[];
};

export type Project = {
  slug: string;
  title: string;
  blurb: string;
  /**
   * Close the flows rows with a rule in the menu. For a project whose prototype
   * leads: the screen and the flows page that reads it are one group, and the
   * line goes under the pair rather than between them.
   */
  flowsDividerAfter?: boolean;
  /**
   * Open as a group in the menu even while it holds one page.
   *
   * The menu makes a project a link at one destination and a heading at two,
   * which is right for a project that happens to have one screen so far. It is
   * wrong for one that is a folder by intent: the pages under it are the point,
   * and a row that names the folder and opens the only page inside it hides
   * that there is an inside.
   */
  folder?: boolean;
  /**
   * Fold the history (the quiet rows under the rule) into a nested Archive
   * row in the menu, so the live work is all that shows until it is opened.
   */
  archive?: boolean;
  /** Which side of the product this touches. Drives the card's accent. */
  surface: Surface;
  /**
   * The member app itself, mounted whole. It goes straight into the prototype
   * and navigates internally, so its screens are not listed anywhere in the
   * nav: they are how you get around inside it, not a menu. Other projects
   * cross-link to them.
   */
  kind?: 'master';
  /** Recorded when the ticket was cut, not read from Linear. */
  ticket?: string;
  /** Handover spec ids. An array because work spanning two surfaces has two. */
  handovers?: string[];
  versions: Version[];
};

const one = (screens: Screen[], note: string): Version[] => [
  { n: 1, note, state: 'backed', screens },
];

export const PROJECTS: Project[] = [
  // The app, first and on its own. Everything below it is work being done to it.
  {
    slug: 'master',
    title: 'Master prototype',
    blurb: 'The member app as it stands. Open it and walk around.',
    surface: 'member',
    kind: 'master',
    versions: [
      {
        n: 1,
        note: 'Kept matching production by hand.',
        state: 'live',
        screens: [
          { slug: 'home', label: 'Home', legacy: '/karehero-app/home' },
          {
            slug: 'actions',
            label: 'Actions',
            legacy: '/karehero-app/actions',
          },
          {
            slug: 'profile',
            label: 'Profile',
            legacy: '/karehero-app/profile',
            note: 'The care record: calls, docs, forms, tools. Was the Care tab.',
          },
          {
            slug: 'care',
            label: 'Care',
            legacy: '/karehero-app/care',
            note: 'Arranging care: the shortlists the care expert has published.',
          },
          {
            slug: 'notifications',
            label: 'Notifications',
            legacy: '/karehero-app/notifications',
          },
          { slug: 'chat', label: 'Chat', legacy: '/karehero-app/chat' },
          {
            slug: 'settings',
            label: 'Settings',
            legacy: '/karehero-app/settings',
          },
          {
            slug: 'shortlists',
            label: 'Shortlists',
            legacy: '/karehero-app/shortlists-v1',
          },
          {
            slug: 'financial-assessment',
            label: 'Financial status',
            legacy: '/karehero-app/forms/financial-assessment',
          },
          {
            slug: 'onboarding/intro',
            label: 'Intro carousel',
            legacy: '/karehero-app/intro',
          },
          {
            slug: 'onboarding/welcome',
            label: 'Welcome',
            legacy: '/karehero-app/welcome',
          },
          {
            // The four-step mock, which is a proposal rather than a step in the
            // walk: production stays on Kinde's pages, and the walk goes
            // through them. Named so it does not read as the sign-up.
            slug: 'onboarding/signup',
            label: 'Sign-up (4-step mock)',
            legacy: '/karehero-app/signup-v2',
          },
          {
            slug: 'onboarding/verify-work-email',
            label: 'Verify work email',
            legacy: '/karehero-app/verify-work-email',
          },
          {
            slug: 'onboarding/verification-success',
            label: 'Verification success',
            legacy: '/karehero-app/verification-success',
          },
          {
            slug: 'onboarding/intake',
            label: 'Tell us once',
            legacy: '/karehero-app/intake',
          },
          {
            slug: 'onboarding/invite-code',
            label: 'Invite code',
            legacy: '/karehero-app/invite-code',
          },
          {
            slug: 'onboarding/partner-code',
            label: 'Partner code',
            legacy: '/karehero-app/partner-code',
          },
          {
            slug: 'onboarding/payroll-id',
            label: 'Payroll id',
            legacy: '/karehero-app/payroll-id',
          },
          {
            slug: 'onboarding/access',
            label: 'Access',
            legacy: '/karehero-app/access',
          },
          {
            slug: 'onboarding/creating-account',
            label: 'Creating account',
            legacy: '/karehero-app/creating-account',
          },
          {
            slug: 'onboarding/signin',
            label: 'Sign in',
            legacy: '/karehero-app/signin',
          },
          {
            slug: 'onboarding/forgot',
            label: 'Forgot password',
            legacy: '/karehero-app/forgot',
          },
          {
            slug: 'onboarding/questions',
            label: 'Onboarding questions',
            legacy: '/karehero-app/onboarding',
          },
          {
            slug: 'onboarding/current',
            label: 'Current flow',
            legacy: '/karehero-app/onboarding-current',
          },
          {
            // Kinde's hosted pages: what production does, and what Create
            // account opens in the walk. It was called Current sign-up, which
            // is true of production and reads, halfway through signing up, as
            // if the walk has fallen into the old flow.
            slug: 'onboarding/current-signup',
            label: 'Create account (Kinde)',
            legacy: '/karehero-app/signup',
          },
        ],
      },
    ],
  },

  // The flows the app already runs, beside the prototype rather than inside it.
  // The master's row is a link that opens the app and you walk around in it;
  // hanging pages under that row would make walking and reading the same
  // control. So the flows sit next to it, and each page is one flow drawn out.
  {
    slug: 'master-flows',
    title: 'Masters',
    blurb: 'A whole journey on one page, pulled from the projects that own it.',
    folder: true,
    surface: 'member',
    // Getting in, the account, the questions after it, then every other route
    // in. It was one Onboarding page running the first three together, which is
    // the journey as somebody walks it but not as anybody works on it: the three
    // belong to different projects, move at different speeds and are read by
    // different people.
    //
    handovers: [
      'master-pre-registration',
      'master-registration',
      'master-onboarding',
      'master-actions',
      'master-profile',
      'master-calls',
      'master-docs',
      'master-settings',
      'master-care-circles',
      'master-help',
      'master-benefits-calculator',
      'master-attendance-allowance',
    ],
    versions: one([], 'The flows as they ship.'),
  },

  // Notion's six projects, in its order.
  {
    // The slug stays 'onboarding': it is the URL every link to this project
    // uses, and the name on the card is the thing that was wrong.
    slug: 'onboarding',
    title: 'Getting in',
    blurb:
      'The front door: signing up, signing in, access checks, and the questions after.',
    surface: 'member',
    // Concepting stays first in the array because a bare /onboarding/flows
    // redirects to handovers[0] and that link has been shared. It is not first
    // in the menu: it is quiet, so it sits under the rule with the history while
    // Sign up leads. Array order and menu order are different questions.
    // Onboarding variants beside the concepting: it is early thinking about
    // getting in, so it reads as quietly as the concepting does.
    handovers: [
      'onboarding',
      'pre-sign-up',
      'sign-up',
      'forgot-password',
      'onboarding-variants',
    ],
    // The rule goes under Sign up, closing the live work off from what it
    // overtook.
    flowsDividerAfter: true,
    versions: one(
      [
        {
          // Its own prototype, the way Tell Us Once has one, rather than a link
          // into the master. Two rows opening the same screen read as the same
          // prototype twice, and the master's walk carries on into Tell Us
          // Once, which is a different project.
          //
          // This one starts at the intro and stops at You're in, which is where
          // getting in stops.
          slug: 'prototype',
          label: 'Prototype',
          leads: true,
        },
      ],
      'Work email verification, invite and partner codes, payroll id, then the questions.',
    ),
  },
  {
    slug: 'tell-us-once',
    title: 'Tell us once',
    blurb:
      'Ask once, reuse the answers across every benefit a client could claim.',
    surface: 'member',
    // Concepting stays first: a bare /tell-us-once/flows redirects to
    // handovers[0], and that link has been shared. It is quiet, so it renders
    // below the rule whatever its place in this list.
    handovers: [
      'v1-intake',
      'intake-profile-review',
      'patterns',
      'onboarding-1-1',
      // Every version of onboarding from 2.0 on, opening on the newest. The
      // pages before and beside it keep their routes for links already shared
      // and are unlisted: see UNLISTED_FLOWS.
      'onboarding-chat',
      'onboarding-2-0',
      'onboarding-2-1',
      // Patterns the same way: every version on one page.
      'onboarding-patterns',
      'patterns-2-0',
      'interactions-2-0',
      // The A/B test against the form, last in the group: it has not been
      // talked through yet. After Interactions, so it lands under Components.
      'onboarding-ab-test',
      'cam-overview',
      'benefits-calculator',
      // The 5 October proposal, paperwork first: a concept since 1.0.
      'benefits-calculator-concept',
      'attendance-allowance',
      // Dynamic flows last: not started, so after the calculator, the way the
      // plan orders them.
      'dynamic-flow',
      // The next conversation after onboarding: dynamic flows and the
      // calculator, cut into slices.
      'dynamic-flow-slices',
      // What the slices mean for Home, actions, profile and the rest.
      'dynamic-flow-app',
      // Profile folded into Care: one hub per person, no toggles.
      'joining-up',
      // When they need more than one thing: the whiteboard's brainstorm, drawn.
      'dynamic-flow-brainstorm',
    ],
    flowsDividerAfter: true,
    archive: true,
    versions: one(
      [
        {
          // Above the prototype, because it is what the prototype is in service
          // of. Every other page in this project covers one piece and is
          // written as though the rest already exist; this is the thing they
          // are pieces of.
          //
          // `leads` rows render in manifest order. See projectHref for what
          // the project's row opens.
          slug: 'plan',
          label: 'Plan',
          leads: true,
          plan: true,
        },
        {
          // The flows page carries the work's name; this is just the thing you
          // can click through, so it says so. Under the plan, opening
          // on the newest onboarding. First in the Onboarding 2 group, so the
          // thing you click through sits with the pages that describe it; no
          // rule under it, which would split the group.
          slug: 'intake-profile-review',
          label: 'Prototype',
          leads: true,
        },
        {
          // For people outside the team: the prototype beside plain-language
          // notes on what they would ask of it. Archived: not leading, so it
          // folds into Archive with the history. The link still works.
          slug: 'walkthrough',
          label: 'Walkthrough',
          // Stages was the same deck with other slides, and is gone.
          legacyPaths: ['/tell-us-once/onboarding-stages'],
        },
        {
          // The Care hub concept presented a screen at a time, with its
          // reasoning: the walkthrough's deck with the hub in the phone.
          slug: 'joining-up-walkthrough',
          label: 'Joining up walkthrough',
          // Named for the Care hub before the page was renamed.
          legacyPaths: ['/tell-us-once/care-hub-walkthrough'],
        },
        {
          // Every component 2.0 uses, each drawn on its own, with its code.
          slug: 'components',
          label: 'Components',
          legacyPaths: ['/tell-us-once/build'],
          leads: true,
          follows: 'interactions-2-0',
        },
        {
          // The CAM side of this project. Its own screen rather than a
          // cross-link into CAM Admin: a cross-link never lights up in the menu
          // (the owning project's row is the one that does), and this project
          // needs somewhere to take the CAM view that is not CAM Admin's page.
          //
          // It mounts the same control panel, opened on Overview with an intake
          // behind it. CAM Admin's entry opens it on Care search with no
          // intake, which is the overview as it ships. Both read the URL, so a
          // ?tab or ?intake in a shared link still wins.
          slug: 'cam-prototype',
          label: 'CAM prototype',
        },
        {
          slug: 'exploration',
          label: 'Exploration',
          legacy: '/explore/tell-us-once',
        },
        {
          slug: 'ai-chat',
          label: 'AI chat',
          legacy: '/north-star',
          variantOf: 'keni',
          variantLabel: 'The whole journey',
          note: 'The AI-first journey end to end: landing, triage, the claim reveal, then the home shell it leaves you in. A stage switcher on the frame jumps between those four and pauses the triage. This is the one about what the app becomes.',
        },
        {
          slug: 'benefits-journey',
          label: 'Benefits journey',
          legacy: '/benefits-flow',
          variantOf: 'keni',
          variantLabel: 'Benefits only',
          note: 'The same opening screen, then only the benefits conversation: Keni asks the screener questions in chat and the claims build up as it goes. Nothing after the reveal. This is the one about whether the conversation can replace the form.',
        },
        {
          slug: 'benefits-calculator',
          label: 'Calculator exploration',
          legacy: '/explore/tell-us-once/benefits-calculator',
        },
      ],
      'Exploration: the claim flow, the calculator, and the AI-first cut of both.',
    ),
  },
  {
    slug: 'project-nails',
    title: 'Project Nails',
    blurb:
      'Rewards and fintech: the care account, refinancing, and the partner work behind it.',
    surface: 'member',
    versions: one([], 'Discovery only. Nothing prototyped yet.'),
  },
  {
    slug: 'project-mc',
    title: 'Project MC',
    blurb:
      'A provider pays to reach the carer, so publishing a shortlist is the send.',
    surface: 'both',
    // A page per side rather than one page with three journeys in it. The rule
    // under them closes the live work off from the two screens other projects
    // own.
    handovers: ['project-mc-provider', 'project-mc-cam', 'project-mc-member'],
    flowsDividerAfter: true,
    versions: one(
      [
        {
          // One thing you open and walk, with the screens on a selector above
          // the frame, the way Tell Us Once and Getting in do it. Three takes
          // on one flow is a screen picker, not three menu rows.
          slug: 'shortlists-tab',
          fills: true,
          label: 'Prototype',
          leads: true,
          variantOf: 'project-mc',
          variantLabel: 'CAM',
          legacy: '/paywall-shortlist',
        },
        // A cross-link, not a second home: CAM Admin owns notifications and the
        // URL stays there. It is listed here because publishing a shortlist is
        // what fires them, so the CAM half of this flow does not end at the tab.
        {
          slug: 'cam-notifications',
          label: 'CAM notifications',
          canonical: 'cam-admin/notifications',
        },
        {
          slug: 'provider-screens',
          fills: true,
          label: 'Provider screens',
          variantOf: 'project-mc',
          variantLabel: 'Provider',
          legacy: '/paywall-provider',
        },
        {
          // In the select as the family's side of the flow. It is the master
          // app's screen and stays at its URL, so picking it leaves Project MC:
          // the same one-way trip the menu row was, in a better place.
          slug: 'member-shortlists',
          label: 'Member shortlists',
          variantOf: 'project-mc',
          variantLabel: 'Member',
          canonical: 'master/shortlists',
        },
        {
          slug: 'exploration',
          label: 'Exploration',
          legacy: '/explore/blurred-profile-paywall',
        },
      ],
      'Three sides of one flow: the CAM, the provider, the family.',
    ),
  },
  {
    slug: 'cam-admin',
    title: 'CAM Admin',
    blurb: 'The CAM-facing admin surface: control panel, care circles, calls.',
    surface: 'cam',
    handovers: ['cam-care-search', 'notifications', 'caseloads'],
    versions: one(
      [
        {
          slug: 'control-panel',
          label: 'Control panel',
          legacy: '/care-search',
        },
        {
          slug: 'care-circles',
          label: 'Care circles',
          legacy: '/care-circles',
        },
        {
          slug: 'notifications',
          label: 'Notifications',
          legacy: '/care-search/notifications',
        },
        {
          slug: 'descoping',
          label: 'Descoping (July 2026)',
          legacy: '/descoping-cam',
        },
      ],
      'The control panel and the surfaces around it.',
    ),
  },
  {
    // The member side of care search: what a family does with a shortlist once
    // their care expert has published one. Its two pages spent a while under
    // Project MC, which owns the paywall rather than this.
    slug: 'care-search',
    title: 'Care search',
    blurb:
      'Browsing a published shortlist: provider detail, securing a home, booking a visit.',
    surface: 'member',
    ticket: 'ENG-3451',
    handovers: ['care-shortlists-v1', 'care-shortlists'],
    versions: one(
      [
        {
          slug: 'shortlists',
          label: 'Shortlists',
          canonical: 'master/shortlists',
        },
      ],
      'V1 is handed over; V2 adds search inside the app.',
    ),
  },
  {
    slug: 'meaningful-interactions',
    title: 'Meaningful Interactions',
    blurb:
      'A carer has a call, the advisor writes notes, the notes become actions, an action points at a document.',
    surface: 'both',
    ticket: 'ENG-3446',
    handovers: [
      'actions',
      'care-calls',
      'calls',
      'care-documents',
      'documents',
    ],
    versions: one(
      [
        { slug: 'actions', label: 'Actions', canonical: 'master/actions' },
        {
          slug: 'profile',
          label: 'Profile overview',
          // Call notes and documents live on the Profile tab. This project is
          // that chain, so it points there rather than at the Care tab, which is
          // shortlists.
          canonical: 'master/profile',
        },
        {
          slug: 'control-panel',
          label: 'CAM control panel',
          canonical: 'cam-admin/control-panel',
        },
      ],
      'Actions, call notes and documents, as one chain.',
    ),
  },

  // Real work, not in Notion's project list because they predate it.
  {
    slug: 'financial-status',
    title: 'Financial status',
    blurb: 'The financial assessment form, one question per screen.',
    surface: 'member',
    handovers: ['financial-assessment'],
    versions: one(
      [
        {
          slug: 'form',
          label: 'Financial status form',
          canonical: 'master/financial-assessment',
        },
      ],
      'One question per screen, with a review step.',
    ),
  },

  // Not projects, but the prototypes exist and need a home. Last, so the work
  // reads first.
  {
    slug: 'design-system',
    title: 'Design system',
    blurb: 'Theme and component work headed for llama.',
    surface: 'other',
    ticket: 'ENG-3445',
    versions: one(
      [{ slug: 'sandbox', label: 'Component sandbox', legacy: '/playground' }],
      'Live theme and component sandbox.',
    ),
  },
  {
    slug: 'sandbox',
    title: 'Sandbox',
    blurb: 'Loose prototypes and starting points.',
    surface: 'other',
    versions: one(
      [
        // Leads: it is a whole app rather than a loose screen, and the rule
        // under it keeps it from reading as one of the odds and ends.
        {
          slug: 'architecture',
          label: 'Architecture',
        },
        // The same app with Care and Profile as one tab, and switching person
        // switching every tab. Beside it, so the two can be tapped between.
        {
          slug: 'architecture-care',
          label: 'Architecture, Care as one',
          dividerAfter: true,
        },
        // Marketing asks for product pictures every few weeks, so the frames
        // live here rather than being rebuilt in Figma each time.
        {
          slug: 'site-mockups',
          label: 'Site mockups',
        },
        // Chloe's website imagery brief (Miro, Oct 2026): banners, carousels
        // and sector pullouts, all on clear grounds for Zippy to assemble.
        {
          slug: 'site-headers',
          label: 'Website imagery',
          dividerAfter: true,
        },
        {
          slug: 'pet-claude',
          label: 'Claude emoji',
        },
        // A llama on the Care hub's Home, to play with.
        {
          slug: 'llama',
          label: 'Llama',
        },
        {
          slug: 'product-design-meme',
          label: '#product-design meme',
          dividerAfter: true,
        },
        {
          slug: 'magic-capture',
          label: 'Magic capture',
          legacy: '/magic-capture',
          nested: true,
        },
        {
          slug: 'blank-device',
          label: 'Blank phone',
          legacy: '/james-playground',
          dividerAfter: true,
        },
        // Jiameng's onboarding voice agent, rebuilt from one test call while
        // the hosted agent is down, to design the next step against.
        {
          slug: 'voice-agent-demo',
          label: 'Voice agent demo',
        },
        // How a caller checks a voice agent stage once it ends, drawn for
        // Health. From the voice agent spec for Attendance Allowance.
        {
          slug: 'voice-agent-health-summary',
          label: 'Voice agent: Health summary',
          dividerAfter: true,
        },
        // A family member prepares an Attendance Allowance claim with their
        // Care Adviser: one voice note, a few taps, the official AA1 filled
        // from checked answers. Named for who it is for, so it reads apart from
        // Call-to-Claim below it, which is the adviser uploading a call.
        {
          slug: 'family-aa-claim',
          label: 'Family AA claim',
          fills: true,
          variantOf: 'family-aa-claim',
          variantLabel: 'Family',
        },
        {
          slug: 'family-aa-claim-adviser',
          label: 'Family AA claim: adviser',
          fills: true,
          variantOf: 'family-aa-claim',
          variantLabel: 'Care Adviser',
        },
        {
          slug: 'family-aa-claim-flows',
          label: 'Family AA claim: flows',
          dividerAfter: true,
        },
        // Call-to-Claim: an adviser uploads an AA call and gets the filled
        // government PDF back. Four screens that walk end to end, so the rule
        // above them keeps them reading as one flow rather than as four loose
        // prototypes.
        {
          slug: 'portal',
          label: 'IFA portal',
          legacy: '/aa-claim',
          legacyPaths: ['/ifa-portal', '/ifa-portal/portal'],
        },
        {
          slug: 'upload',
          label: 'Upload',
          legacy: '/aa-claim/upload',
          legacyPaths: ['/ifa-portal/upload'],
        },
        {
          slug: 'processing',
          label: 'Processing',
          legacy: '/aa-claim/processing',
          legacyPaths: ['/ifa-portal/processing'],
        },
        {
          slug: 'review',
          label: 'Review',
          legacy: '/aa-claim/review',
          legacyPaths: ['/ifa-portal/review'],
        },
      ],
      'Whatever someone started and has not filed yet, and Call-to-Claim.',
    ),
  },
];

// ---------------------------------------------------------------------- helpers

/** The colour a project is tinted with, derived from the surface it touches. */
export const accentOf = (p: Project): Accent => SURFACE[p.surface].accent;

export const findProject = (slug: string): Project | undefined =>
  PROJECTS.find((p) => p.slug === slug);

export const master = (): Project =>
  PROJECTS.find((p) => p.kind === 'master') as Project;

/** The version a version-less URL resolves to: backed, else live, else first. */
export const sourceOfTruth = (p: Project): Version =>
  p.versions.find((v) => v.state === 'backed') ??
  p.versions.find((v) => v.state === 'live') ??
  p.versions[0];

export const isVersioned = (p: Project): boolean => p.versions.length > 1;

/**
 * Where a screen lives. A cross-link resolves to the path it names; everything
 * else is `/[project]/[screen]`, with the screen segment dropped when a version
 * holds only one, so the project and the screen share a URL.
 */
export const screenPath = (p: Project, v: Version, s: Screen): string => {
  if (s.canonical) return `/${s.canonical}`;
  const versionSegment =
    isVersioned(p) && v.n !== sourceOfTruth(p).n ? `/v${v.n}` : '';
  const screenSegment =
    v.screens.length > 1 || p.kind === 'master' ? `/${s.slug}` : '';
  return `/${p.slug}${versionSegment}${screenSegment}`;
};

/**
 * A flows page's path. The id is kept only when a project has several.
 *
 * Called flows rather than handover because that is what the page is: every
 * screen of the work and every state of each one. "Handover" named the moment it
 * was passed to engineering, which is one day in its life and not what you open
 * it for the rest of the time.
 */
export const handoverPath = (p: Project, id: string): string =>
  `/${p.slug}/flows${(p.handovers?.length ?? 0) > 1 ? `/${id}` : ''}`;

/**
 * What distinguishes one Concepting page from another, for the projects that have
 * several. Every flows page is called Concepting; a project with more than one
 * says which, because three rows reading Concepting name nothing.
 *
 * Spelled out rather than derived from the id: prettifying would give
 * "Cam care search" for a page about care search and shortlists.
 */
const FLOWS_LABELS: Record<string, string> = {
  'project-mc': 'provider paywall',
  'cam-care-search': 'care search',
  notifications: 'notifications',
  caseloads: 'caseloads',
  actions: 'actions',
  'care-calls': 'call notes (member)',
  calls: 'call notes (CAM)',
  'care-documents': 'documents (member)',
  documents: 'documents (CAM)',
  'care-shortlists': 'shortlists v2',
  'care-shortlists-v1': 'shortlists v1',
};

/**
 * The page's own name, when Concepting is not what it is. Intake, profile and
 * review sits beside Concepting in the same project: one is the thinking, the
 * other is what the build is getting.
 */
const FLOWS_NAMES: Record<string, string> = {
  'master-pre-registration': 'Pre-registration',
  'master-registration': 'Registration',
  'master-onboarding': 'Onboarding',
  'master-actions': 'Actions',
  'master-profile': 'Profile',
  'master-calls': 'Calls',
  'master-docs': 'Docs',
  'master-settings': 'Settings',
  'master-help': 'Help',
  'master-benefits-calculator': 'Benefits calculator',
  'master-attendance-allowance': 'Attendance Allowance',
  'master-care-circles': 'Care circles',
  'v1-intake': 'Intake, profile and review',
  'onboarding-variants': 'Onboarding variants',
  patterns: 'Patterns 3.0',
  'patterns-2-0': 'Patterns 4.0',
  'interactions-2-0': 'Interactions',
  'project-mc-provider': 'Provider flow',
  'project-mc-cam': 'CAM flow',
  'project-mc-member': 'Member flow',
  'intake-profile-review': 'Onboarding 3.0',
  'onboarding-1-1': 'Onboarding 3.1',
  'onboarding-2-0': 'Onboarding 4.0',
  'onboarding-2-1': 'Onboarding 4.1',
  // Inside the Onboarding group, so it says what it is rather than repeating it.
  'onboarding-chat': 'Flows',
  'onboarding-ab-test': 'A/B test',
  'onboarding-patterns': 'Patterns',
  'dynamic-flow': 'Concepting',
  'dynamic-flow-slices': 'In slices',
  'dynamic-flow-app': 'Across the app',
  'joining-up': 'Joining up',
  'dynamic-flow-brainstorm': 'Brainstorm',
  'cam-overview': 'CAM overview',
  // Inside the Benefits calculator group, so it says what it is.
  'benefits-calculator': 'Flows',
  'benefits-calculator-concept': 'Paperwork first',
  'attendance-allowance': 'Attendance allowance',
  'pre-sign-up': 'Pre-sign-up',
  'sign-up': 'Sign up',
  'forgot-password': 'Forgot password',
};

/**
 * Flows pages that have been overtaken. They keep their place in the menu, under
 * the rule with the older explorations rather than up with the live work, and
 * they read as quietly as the rest of the history.
 *
 * Nothing is deleted: the thinking is why the current design is the shape it is,
 * and a project that hides how it got here has to have that conversation again.
 */
const QUIET_FLOWS = new Set(['v1-intake', 'onboarding', 'onboarding-variants']);

/**
 * Flows pages that keep their route and stay out of the menu.
 *
 * Not the same as quiet. A quiet page is history and reads like it; an unlisted
 * one is not ready to be found by somebody who was not sent it. The link works,
 * so it can be shared and the deployed page is the thing being looked at, and
 * nothing about it is deleted.
 *
 * Take the id out of here to list it. Do not take it out of the project's
 * `handovers`: the route and the menu are generated from that one array, so
 * removing it there takes the URL away with the row, and links shared in the
 * meantime would land on the catch-all.
 */
export const UNLISTED_FLOWS = new Set([
  // Onboarding's versions, reached from the versioned page's history rather
  // than a row each. 1.0 and Patterns 1.0 are the first major's; 2.0 and 2.1
  // are on the versioned page itself.
  'intake-profile-review',
  'patterns',
  'onboarding-1-1',
  'onboarding-2-0',
  'onboarding-2-1',
  // On the versioned Patterns page, with 1.0.
  'patterns-2-0',
]);

/**
 * Flows pages that close their group with a rule, the way `Screen.dividerAfter`
 * does for a screen.
 */
const FLOWS_DIVIDER_AFTER = new Set([
  'cam-overview',
  // The Onboarding group closes under the A/B test, its last row. The
  // benefits calculator starts the next.
  'onboarding-ab-test',
  // Help closes the app's own tabs. What follows is the claims the app helps
  // you make, which is a different kind of thing.
  'master-help',
]);

/**
 * Rows folded under a heading of their own in the menu, the way Archive folds
 * the history, keyed by project. Screens and flows pages are listed apart
 * because a screen can share its slug with a flows page: Tell Us Once's
 * Prototype is `intake-profile-review`, and so is Onboarding 1.0.
 *
 * The rows keep their place in the menu's order, so a group's rows need to sit
 * together in it. `open` starts the group open.
 */
type MenuRows = { flows?: string[]; screens?: string[] };

export const MENU_GROUPS: Record<
  string,
  (MenuRows & {
    label: string;
    open?: boolean;
    /**
     * The group's early designs, not for build: folded under a quieter
     * Concepts row at the foot of the group, so they sit with the work they
     * were for without reading as part of it.
     */
    concepts?: MenuRows;
  })[]
> = {
  'tell-us-once': [
    {
      // Onboarding and the pages that go with it. No number: which version
      // you are looking at is the version picker's job, on each page.
      label: 'Onboarding',
      flows: [
        'onboarding-chat',
        'onboarding-patterns',
        'interactions-2-0',
        'onboarding-ab-test',
      ],
      screens: ['intake-profile-review', 'components'],
      concepts: { flows: ['v1-intake'] },
      open: true,
    },
    {
      label: 'Benefits calculator',
      flows: ['benefits-calculator'],
      concepts: {
        flows: ['benefits-calculator-concept'],
        screens: ['benefits-calculator'],
      },
    },
    {
      // The engine and what it means for the app, ending in the hub. Newest
      // last, so it carries the highest number. Open, as the work in hand.
      label: 'Dynamic flows',
      open: true,
      concepts: {
        flows: [
          'dynamic-flow',
          'dynamic-flow-slices',
          'dynamic-flow-app',
          'dynamic-flow-brainstorm',
          'joining-up',
        ],
        screens: ['joining-up-walkthrough'],
      },
    },
  ],
};

const groupOf = (
  project: string,
  kind: 'flows' | 'screens',
  id: string,
): string | undefined =>
  MENU_GROUPS[project]?.find(
    (g) => g[kind]?.includes(id) || g.concepts?.[kind]?.includes(id),
  )?.label;

/**
 * A concept's number within its section, from 1, in the order its section
 * lists its concepts (flows pages, then screens). Undefined for anything that
 * is not a concept.
 */
export const conceptNumber = (
  project: string,
  kind: 'flows' | 'screens',
  id: string,
): number | undefined => {
  for (const g of MENU_GROUPS[project] ?? []) {
    if (!g.concepts?.[kind]?.includes(id)) continue;
    const order = [
      ...(g.concepts.flows ?? []).map((x) => `flows:${x}`),
      ...(g.concepts.screens ?? []).map((x) => `screens:${x}`),
    ];
    return order.indexOf(`${kind}:${id}`) + 1;
  }
  return undefined;
};

const isConcept = (
  project: string,
  kind: 'flows' | 'screens',
  id: string,
): boolean =>
  (MENU_GROUPS[project] ?? []).some((g) => g.concepts?.[kind]?.includes(id));

/**
 * Rows that render under the project's rule instead of above it, in the order
 * given, keyed by project. Each entry is a screen slug or a flows page id.
 *
 * Tell Us Once is why. Its rule separated the current work from the history, and
 * the CAM side of the project is neither: it is live work, and it is not the
 * member journey the rows above it walk. Listing it under the line groups the
 * two CAM rows together and keeps the member flow reading as one sequence.
 *
 * So the rule now means "the member journey ends here" rather than "history
 * below". Anything placed here is above the history and reads at full strength;
 * `QUIET_FLOWS` is still the way to say something has been overtaken.
 */
const BELOW_RULE: Record<string, string[]> = {
  'tell-us-once': ['cam-prototype', 'cam-overview'],
};

export type Destination = {
  label: string;
  path: string;
  isFlows: boolean;
  /** A flows page that has been overtaken. See QUIET_FLOWS. */
  quiet?: boolean;
  /**
   * The screen belongs to another project, so this row points at it rather than
   * owning it. It never draws as selected: the row that does is the one in the
   * project the screen lives in, and two rows lit at once says the same screen
   * is in two places.
   */
  crossLink?: boolean;
  /** Close the group with a rule under this row. */
  dividerAfter?: boolean;
  /** Sits above the flows rows, and reads as prominently. See Screen.leads. */
  leads?: boolean;
  /** The project's plan page. See Screen.plan. */
  plan?: boolean;
  /**
   * Placed under the project's rule on purpose. See BELOW_RULE.
   *
   * The menu reads it as live work, which is the whole reason the field exists:
   * below the line otherwise means history, and a screen row otherwise reads
   * quiet. A placed row is neither.
   */
  placed?: boolean;
  /** Listed inside the project's Archive row. See Project.archive. */
  archived?: boolean;
  /** Folded under a heading of this name, the way Archive is. See MENU_GROUPS. */
  group?: string;
  /** One of its group's early designs, under the group's Concepts row. */
  concept?: boolean;
};

/**
 * Everything a project offers the nav: a screen marked `leads`, then its flows
 * pages, then the rest of its screens.
 *
 * Flows lead because a flows page is the whole of the work in one place, which
 * is what you want when you open a project rather than one screen of it. The
 * screens under it are the individual things it collects. A screen can opt out
 * with `leads` when the prototype is the thing and the flows page is the reading
 * of it.
 *
 * They are in here at all because for a while they were nowhere: the old menu
 * had a Handovers zone and the project page listed them, both went, and thirteen
 * pages quietly became reachable by URL only, including one being written that
 * week.
 */
export const destinations = (p: Project): Destination[] => {
  const v = sourceOfTruth(p);
  // One row per variant group, not one per take: the others are reached by
  // the exploration select on the page itself, so listing them again in the
  // menu is the same prototype twice.
  const screens = v.screens.filter(
    (s, i) =>
      !s.variantOf ||
      v.screens.findIndex((o) => o.variantOf === s.variantOf) === i,
  );

  const screenDest = (s: Screen): Destination => ({
    label: s.label,
    path: screenPath(p, v, s),
    isFlows: false,
    dividerAfter: s.dividerAfter,
    leads: s.leads,
    plan: s.plan,
    crossLink: Boolean(s.canonical),
    group: groupOf(p.slug, 'screens', s.slug),
    concept: isConcept(p.slug, 'screens', s.slug),
  });

  const flowIds = (p.handovers ?? []).filter((id) => !UNLISTED_FLOWS.has(id));

  const flowDest = (id: string): Destination => ({
    label:
      FLOWS_NAMES[id] ??
      (flowIds.length > 1 && FLOWS_LABELS[id]
        ? `Concepting: ${FLOWS_LABELS[id]}`
        : 'Concepting'),
    path: handoverPath(p, id),
    isFlows: true,
    // Concepts read quieter, like the history, wherever they sit.
    quiet: QUIET_FLOWS.has(id) || isConcept(p.slug, 'flows', id),
    dividerAfter: FLOWS_DIVIDER_AFTER.has(id),
    group: groupOf(p.slug, 'flows', id),
    concept: isConcept(p.slug, 'flows', id),
  });

  // Rows this project has asked to sit under the rule, in the order it asked
  // for. Screens and flows pages can be mixed, so a prototype and the spec
  // reading it can be paired here rather than landing in separate groups.
  const placed = BELOW_RULE[p.slug] ?? [];
  // A bare id places a screen or a flows page, whichever has it. `screen:` or
  // `flows:` says which, for an id both have: the calculator's exploration
  // screen and its flows page are both `benefits-calculator`.
  const isPlacedScreen = (slug: string) =>
    placed.includes(slug) || placed.includes(`screen:${slug}`);
  const isPlacedFlow = (id: string) =>
    placed.includes(id) || placed.includes(`flows:${id}`);
  const below = placed
    .map((entry) => {
      const [kind, id] = entry.includes(':')
        ? (entry.split(':') as ['screen' | 'flows', string])
        : [undefined, entry];
      const screen =
        kind !== 'flows' ? v.screens.find((s) => s.slug === id) : undefined;
      if (screen) return screenDest(screen);
      return kind !== 'screen' && flowIds.includes(id)
        ? flowDest(id)
        : undefined;
    })
    .filter((d): d is Destination => Boolean(d))
    .map((d) => ({ ...d, placed: true }));

  // What the project is now: the prototype, then the flows pages being written
  // against it. Everything overtaken goes below the rule with the explorations,
  // so the rule separates current from history rather than flows from screens.
  // A leads screen that pairs with a flows page goes directly above it
  // instead, and one that follows a flows page directly below it, taking the
  // flows page's rule so the rule still closes the group.
  const live = [
    ...screens
      .filter(
        (s) => s.leads && !s.pairsWith && !s.follows && !isPlacedScreen(s.slug),
      )
      .map(screenDest),
    ...flowIds
      .filter((id) => !QUIET_FLOWS.has(id) && !isPlacedFlow(id))
      .flatMap((id) => {
        const leader = (s: Screen) => s.leads && !isPlacedScreen(s.slug);
        const before = screens.filter((s) => leader(s) && s.pairsWith === id);
        const after = screens.filter((s) => leader(s) && s.follows === id);
        const flow = flowDest(id);
        if (!after.length) return [...before.map(screenDest), flow];
        return [
          ...before.map(screenDest),
          { ...flow, dividerAfter: false },
          ...after.map((s, i) =>
            i === after.length - 1
              ? { ...screenDest(s), dividerAfter: flow.dividerAfter }
              : screenDest(s),
          ),
        ];
      }),
  ];
  const rest = [
    ...flowIds
      .filter((id) => QUIET_FLOWS.has(id) && !isPlacedFlow(id))
      .map(flowDest),
    ...screens
      .filter((s) => !s.leads && !isPlacedScreen(s.slug))
      .map(screenDest),
  ];

  return [
    ...live.map((d, i) =>
      p.flowsDividerAfter && i === live.length - 1
        ? { ...d, dividerAfter: true }
        : d,
    ),
    ...below,
    // A row in a group lives in the group, wherever the history would put it.
    ...rest.map((d) => (p.archive && !d.group ? { ...d, archived: true } : d)),
  ];
};

/**
 * Where a project's entry in the nav goes: straight into a prototype. The master
 * opens the app at Home; everything else opens its first destination, which is
 * its spec when nothing is drawn yet. No page whose only job is to list links.
 */
export const projectHref = (p: Project): string => {
  // The intro, not Home: the master prototype is walked from the front now, and
  // opening it in the middle of the app made the walk something you had to know
  // to find.
  if (p.kind === 'master') return '/master/onboarding/intro';
  // The plan is skipped, however high it reads in the menu. It is the one page
  // in a project that is not a thing to look at, and a card that opens the
  // reasoning instead of the screens is the nav page this scheme got rid of.
  const open = destinations(p).filter((d) => !d.plan);
  return open[0]?.path ?? '/';
};

/** The screens a version switch offers, in manifest order. */
export const variants = (p: Project, v: Version, s: Screen): Screen[] =>
  s.variantOf ? v.screens.filter((o) => o.variantOf === s.variantOf) : [];

export type ResolvedScreen = {
  project: Project;
  version: Version;
  screen: Screen;
  path: string;
};

/** Every screen that owns a URL. Cross-links are not screens here. */
export const allScreens = (): ResolvedScreen[] =>
  PROJECTS.flatMap((project) =>
    project.versions.flatMap((version) =>
      version.screens
        .filter((screen) => !screen.canonical)
        .map((screen) => ({
          project,
          version,
          screen,
          path: screenPath(project, version, screen),
        })),
    ),
  );

/** Where a path that used to exist lives now. */
export const newPathFor = (legacy: string): string | undefined =>
  allScreens().find((r) => r.screen.legacy === legacy)?.path;

/** Old path to new path. Skips anything already in the right place. */
/**
 * Flows pages that moved project, from their old path to their new one. A flows
 * page's path comes from the project that lists it, so moving it changes the
 * URL; this keeps the old one landing.
 */
const MOVED_FLOWS: Record<string, string> = {
  '/master-flows/flows/onboarding-variants':
    '/onboarding/flows/onboarding-variants',
  '/tell-us-once/flows/onboarding-variants':
    '/onboarding/flows/onboarding-variants',
  // Joining up, named the Care hub until the page was renamed.
  '/tell-us-once/flows/care-hub': '/tell-us-once/flows/joining-up',
  '/handover/care-hub': '/tell-us-once/flows/joining-up',
  // The two Home pages, folded into Joining up as its older concepts.
  '/tell-us-once/flows/home-first-visit': '/tell-us-once/flows/joining-up',
  '/handover/home-first-visit': '/tell-us-once/flows/joining-up',
  '/tell-us-once/flows/home-next': '/tell-us-once/flows/joining-up',
  '/handover/home-next': '/tell-us-once/flows/joining-up',
};

export const redirects = (): Record<string, string> => {
  const out: Record<string, string> = { ...MOVED_FLOWS };
  for (const r of allScreens()) {
    for (const was of [r.screen.legacy, ...(r.screen.legacyPaths ?? [])]) {
      if (!was || was === r.path) continue;
      out[was] = r.path;
    }
  }
  for (const p of PROJECTS) {
    for (const id of p.handovers ?? []) {
      const to = handoverPath(p, id);
      // Both the original /handover/<id> and the /flows rename's predecessor, so
      // links shared under either shape still land.
      if (`/handover/${id}` !== to) out[`/handover/${id}`] = to;
      const wasHandover = `/${p.slug}/handover${(p.handovers?.length ?? 0) > 1 ? `/${id}` : ''}`;
      if (wasHandover !== to) out[wasHandover] = to;
    }
    // A project's first handover answered the bare /flows path until a second one
    // arrived and put the id in it. Links shared before that still land on it.
    if ((p.handovers?.length ?? 0) > 1) {
      out[`/${p.slug}/flows`] = handoverPath(p, p.handovers![0]);
    }
  }
  return out;
};
